import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

// This symbol will be used on ObservableObjectChange values to indicate that the property was deleted
export const PROPERTY_DELETED = Symbol.for('observableObject:deleted');

export const ARRAY_ITEM_REMOVED = Symbol.for('observableObject:arrayItemRemoved');

export const isObservableObject = Symbol.for('oservableObject:isObservableObject');
export const observableObjectKeyPath = Symbol.for('observableObject:keyPath');

const observableObjectSpecialSymbolKeyMap = {
  [isObservableObject]: true,
  [observableObjectKeyPath]: true,
} as const;

export type ObservableObjectChange = [keyPath: string, prevValue: any, newValue: any];

export const makeObservableObject = <T extends Record<string, any>>(
  obj: T,
  onChangeListener: (change: ObservableObjectChange) => void,
  baseKeyPath = '',
): T => {
  const childObservableObjectCache: Record<string, object> = {};

  const baseChildKeyPath = baseKeyPath ? `${baseKeyPath}.` : '';

  return new Proxy(obj, {
    set(target, propertyKey: string, newValue, receiver) {
      if (propertyKey in observableObjectSpecialSymbolKeyMap) {
        // Don't allow setting special symbols
        return false;
      }

      const hasProperty = Reflect.has(target, propertyKey);
      let prevValue;
      if (hasProperty) {
        prevValue = Reflect.get(target, propertyKey, receiver);
      } else if (Array.isArray(target) && !Number.isNaN(Number(propertyKey))) {
        // If the object is an array and the property being set is an index number, use the special
        // ARRAY_ITEM_REMOVED symbol to indicate that the item at that index did not exist in the array before
        prevValue = ARRAY_ITEM_REMOVED;
      } else {
        // If the object currently doesn't have the property being set, use the special PROPERTY_DELTED symbol
        // to indicate that the property was completely absent from the object before
        prevValue = PROPERTY_DELETED;
      }

      // Bail if the new value is the same as the previous value
      if (isEqual(prevValue, newValue)) {
        return true;
      }

      if (Reflect.set(target, propertyKey, newValue, receiver)) {
        // Delete any cached observable objects tied to the changed value
        delete childObservableObjectCache[propertyKey];

        const change: ObservableObjectChange = [
          `${baseChildKeyPath}${propertyKey}`,
          cloneDeep(prevValue),
          cloneDeep(newValue),
        ];
        onChangeListener(change);

        return true;
      }

      return false;
    },
    get(target, propertyKey, receiver) {
      if (propertyKey in observableObjectSpecialSymbolKeyMap) {
        if (propertyKey === isObservableObject) {
          // The presence of this symbol indicates that the object is a proxied observable object
          // May be useful in situations where you need to make sure you're working with an un-proxied clone of the object
          return true;
        }
        if (propertyKey === observableObjectKeyPath) {
          // The presence of this symbol indicates that the object is a proxied observable object
          // May be useful in situations where you need to make sure you're working with an un-proxied clone of the object
          return baseKeyPath;
        }
      }

      const value = Reflect.get(target, propertyKey, receiver);

      if (typeof propertyKey === 'string' && typeof value === 'object' && value !== null) {
        if (!childObservableObjectCache[propertyKey]) {
          childObservableObjectCache[propertyKey] = makeObservableObject(
            value,
            onChangeListener,
            `${baseChildKeyPath}${propertyKey}`,
          );
        }
        return childObservableObjectCache[propertyKey];
      }

      return value;
    },
    deleteProperty(target, propertyKey: string) {
      if (propertyKey in observableObjectSpecialSymbolKeyMap) {
        // Don't allow deleting special symbols
        return false;
      }

      const prevValue = Reflect.get(target, propertyKey);
      delete childObservableObjectCache[propertyKey];

      if (Reflect.deleteProperty(target, propertyKey)) {
        let newValue;
        if (Array.isArray(target) && !Number.isNaN(Number(propertyKey))) {
          // If the object is an array and the property being deleted is an index number, use the special
          // ARRAY_ITEM_REMOVED symbol to indicate that the item at that index was removed
          newValue = ARRAY_ITEM_REMOVED;
        } else {
          // For all other objects, use the special PROPERTY_DELTED symbol to indicate that the property was deleted
          newValue = PROPERTY_DELETED;
        }

        const change: ObservableObjectChange = [
          `${baseChildKeyPath}${propertyKey}`,
          cloneDeep(prevValue),
          newValue,
        ];

        onChangeListener(change);
        return true;
      }
      return false;
    },
    has(target, propertyKey) {
      return propertyKey in observableObjectSpecialSymbolKeyMap || Reflect.has(target, propertyKey);
    },
  });
};

export const getIsObservableObject = (obj: object): boolean => {
  return isObservableObject in obj;
};

export const getObservableObjectKeyPath = (obj: object): string | undefined => {
  const keyPath = observableObjectKeyPath in obj ? obj[observableObjectKeyPath] : undefined;
  return typeof keyPath === 'string' ? keyPath : undefined;
};

/**
 * Takes an observable object change and a path to match against, and returns the change if the path matches.
 * If the path does not match, returns null.
 *
 * If the change is a parent of the match path, we will return a finer-grained change for the values at the match path.
 *
 * @example
 * const change = ["a.b.c", { d: 1 }, { d: 2 }];
 * matchObservableObjectChange(change, "a.b.c.d"); // ["a.b.c.d", 1, 2]
 * matchObservableObjectChange(change, "a.b.c"); // ["a.b.c", { d: 1 }, { d: 2 }]
 * matchObservableObjectChange(change, "a.b"); // ["a.b.c", { d: 1 }, { d: 2 }]
 * matchObservableObjectChange(change, "a.b.f"); // null; no match
 */
export const matchObservableObjectChange = (
  change: ObservableObjectChange,
  matchPath: string,
): ObservableObjectChange | null => {
  const [changedKeyPath, prevValue, newValue] = change;

  const matchPathLength = matchPath.length;
  const changedKeyPathLength = changedKeyPath.length;

  if (
    changedKeyPath === matchPath ||
    (changedKeyPathLength > matchPathLength && changedKeyPath.startsWith(matchPath))
  ) {
    // If the match path is fully contained within the changed path, either because it's an exact match or because
    // the changed path goes to a deeper-nested child object, this counts as a match and we can return the change as is.
    return change;
  } else if (changedKeyPathLength < matchPathLength) {
    // If the changed path is shorter than the match path, that means the change occured to a parent of the matching path.
    // We'll dig deeper to see if the change actually updated the value at the match path.
    const doPathsMatch = matchPath.startsWith(changedKeyPath);
    if (!doPathsMatch) {
      return null;
    }

    // If the changed path has length, slice the match path to just the part that is relative to the changed path.
    // We'll also slice off one extra character to account for the dot that separates the changed path from the match path.
    // If the changed path is empty (ie, the full root object changed) we should just use the match path as is.
    const pathToObservedValue =
      changedKeyPathLength > 0 ? matchPath.slice(changedKeyPathLength + 1) : matchPath;
    const relativePreviousValue = has(prevValue, pathToObservedValue)
      ? get(prevValue, pathToObservedValue)
      : PROPERTY_DELETED;
    const relativeNewValue = has(newValue, pathToObservedValue)
      ? get(newValue, pathToObservedValue)
      : PROPERTY_DELETED;

    // Only count this as a match if the value at the path actually changed
    if (!isEqual(relativePreviousValue, relativeNewValue)) {
      return [matchPath, cloneDeep(relativePreviousValue), cloneDeep(relativeNewValue)];
    }
  }

  return null;
};

export const getRelativeKeyPath = (keyPath: string, baseKeyPath: string): string | null => {
  if (keyPath.startsWith(baseKeyPath)) {
    return keyPath.slice(baseKeyPath.length + (keyPath[baseKeyPath.length] === '.' ? 1 : 0));
  }
  return null;
};
